import { Component, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { Router } from '@angular/router';

// ionic
import { Events } from "../../helpers/events";
import { Platform } from "@ionic/angular";
import { ModalController, PopoverController } from '@ionic/angular';

// rxjs
import { Subject, Subscription } from "rxjs";
import { takeUntil, debounceTime } from "rxjs/operators";

// components
import { PasswordModalComponent } from "../../components/password-modal/password-modal.component";
import { ChagGroupsFormComponent } from "../../components/chat-groups-form/chat-groups-form.component";

// services
import { ChatGroupsService } from "../../services/chat-groups.service";
import { EventService } from "../../services/event.service";
import { UserService } from "../../services/user.service";
import { OverlayService } from "../../services/overlay.service";
import { PlatformService } from "../../services/platform.service";
import { RouterExtService } from "../../services/router-ext.service";

// models
import { Event } from "../../models/event";
import { ChatGroup } from "../../models/chat-group";
import { User } from "../../models/user";

@Component({
    selector: 'app-add-chat-group',
    templateUrl: './add-chat-group.component.html',
    styleUrls: ['./add-chat-group.component.scss'],
})
export class AddChatGroupComponent implements OnInit, OnDestroy {

    /**
     * chat box
     *
     * @type ViewChild
     */
    @ViewChild('chatList') private scrollContainer: ElementRef;

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * subscription for event changes subject
     *
     * @type {Subscription}
     */
    private subscriptionEvent: Subscription;

    /**
     * event
     *
     * @type {Event}
     */
    public event: Event;

    /**
     * user
     *
     * @type {User}
     */
    public user: User = new User;

    /**
     * chat group channels
     *
     * @type ChatGroup[]
     */
    public assignedChatGroups: ChatGroup[] = [];

    /**
     * show loading
     *
     * @type {boolean}
     */
    public loading: boolean = true;

    // handlers for events
    eventChangeHandler;
    refreshHandler;

    constructor(
        private appEvents: Events,
        private routerExtService: RouterExtService,
        public platform: Platform,
        public chatGroupsService: ChatGroupsService,
        public eventService: EventService,
        public userService: UserService,
        public router: Router,
        public plt: PlatformService,
        public overlayService: OverlayService,
        public popoverController: PopoverController,
        public modalController: ModalController,
    ) {
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {

        this.eventChangeHandler = () => {
            this.attachEvents();
        }

        // attach hooks on event change
        this.appEvents.subscribe('event:changed', this.eventChangeHandler);

        this.attachEvents();
    }

    attachEvents() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();

        this.ngUnsubscribe = new Subject<void>();

        this.userService.getCurrentUser().pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(0)
        ).subscribe((user) => {
            if (user.id) {
                this.user = user;

                // cancel previous subscription
                if (this.subscriptionEvent) {
                    this.subscriptionEvent.unsubscribe();
                }

                this.subscriptionEvent = this.eventService.getCurrentEvent().pipe(
                    takeUntil(this.ngUnsubscribe),
                    debounceTime(0)
                ).subscribe((event) => {
                    this.event = event;
                    this.refresh();
                });
                this.plt.activeSubscriptions.push(this.subscriptionEvent);

            } else {
                // cancel previous subscription
                if (this.subscriptionEvent) {
                    this.subscriptionEvent.unsubscribe();
                }
            }
        });
    }

    /**
      * refresh list
      *
      * @return void
      */
    public refresh() {
        if (this && this.user && this.user.id) {

            let subscription = this.chatGroupsService.getListByEvent(this.user.selected_participant.event.id).subscribe(
                (success) => {

                    // process chat groups
                    this.assignedChatGroups = [];

                    if (success.chat_groups.length > 0) {
                        success.chat_groups.forEach(
                            (chatGroup) => {
                                if (!chatGroup.hidden) {
                                    this.assignedChatGroups.push(new ChatGroup(chatGroup))
                                }
                            });
                    }
                }
            );
            this.plt.activeSubscriptions.push(subscription);
        }
    }

    /**
      * join chat group
      *
      * @return void
      */
    public joinChatGroup(chatGroup: ChatGroup, password?): void {
        let subscription = this.chatGroupsService.joinChatGroup(chatGroup.id, password).subscribe(
            (success) => {
                this.refresh();
                this.appEvents.publish('messages:refresh:list', chatGroup.id);
            },
            (error) => {
                const data = error.error;
                this.overlayService.showError(data.message);
            }
        );
        this.plt.activeSubscriptions.push(subscription);
    }

    /**
     * open and process modal with password
     */
    async openPasswordModal(ev: any, chatGroup: ChatGroup) {

        const popover = await this.popoverController.create({
            component: PasswordModalComponent,
            event: ev,
            componentProps: {},
            cssClass: 'edit-note'
        });

        popover.onWillDismiss().then((data) => {
            if (data.data && data.data.password) {
                this.joinChatGroup(chatGroup, data.data.password);
            }
        });

        return await popover.present();
    }

    /**
     * open modal with chat group craeting form
     */
    async addChatGroup() {

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: ChagGroupsFormComponent,
            componentProps: {},
            cssClass: 'chat-group-form'
        });

        modal.onWillDismiss().then((data) => {

            // refresh list with chat groups
            if (data.data?.chatGroupId) {
                setTimeout(() => {
                    this.appEvents.publish('messages:refresh:list', data.data.chatGroupId);
                    // this.router.navigate([this.plt.defaultLink + '/messages/channel/' + data.data.chatGroupId]);
                }, 250);
            } else {
                if (data.data?.action == 'close' || data.role == 'backdrop') {
                    this.routerExtService.softBack();
                }
            }

        });

        return await modal.present();
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy(): void {
        if (this.eventChangeHandler) {
            this.appEvents.unsubscribe('event:changed', this.eventChangeHandler);
        }

        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

}
